/**
 * SSOCallbackPage — frontend landing point after a successful OAuth redirect.
 *
 * The backend redirects here with `#token=...&user=...` in the URL fragment.
 * Fragments never reach the server or show up in access logs, so this is a
 * safe way to hand a JWT to the SPA.
 *
 * We read the fragment, populate the auth store exactly like a password-+-MFA
 * login would, then navigate to the dashboard.
 */
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, Loader2 } from 'lucide-react'
import { useAuth } from '@/hooks/useAuth'

export default function SSOCallbackPage() {
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fragment = window.location.hash.replace(/^#/, '')
    if (!fragment) {
      setError('Missing authentication data')
      return
    }
    const params = new URLSearchParams(fragment)
    const token = params.get('token')
    const userParam = params.get('user')

    if (!token || !userParam) {
      setError('Invalid authentication response')
      return
    }

    let userData: any
    try {
      userData = JSON.parse(decodeURIComponent(userParam))
    } catch {
      setError('Could not decode user info')
      return
    }

    // Persist the JWT + user in the Zustand auth store by directly mutating
    // setState. We bypass the login/verifyMFA actions because this flow is
    // already past both steps.
    useAuth.setState({
      user: {
        id: userData.user_id,
        name: userData.name,
        email: userData.email,
        role: userData.role,
        company: userData.company ?? '',
        title: userData.title ?? '',
        permissions: userData.permissions,
        must_change_password: userData.must_change_password ?? false,
        is_platform_admin: userData.is_platform_admin ?? false,
      },
      accessToken: token,
      isAuthenticated: true,
      mfaToken: null,
      loading: false,
      error: null,
    })
    localStorage.setItem('av-access-token', token)

    // Scrub the fragment so the token doesn't sit in history
    window.history.replaceState(null, '', '/app')
    navigate('/app', { replace: true })
  }, [navigate])

  if (error) {
    return (
      <div className="min-h-screen bg-base flex items-center justify-center p-4">
        <div className="card p-6 max-w-sm w-full">
          <div className="flex items-start gap-2 text-rose-light">
            <AlertCircle size={18} className="shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-text-primary">Sign-in failed</p>
              <p className="text-xs text-text-muted mt-1">{error}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/login', { replace: true })}
            className="btn-primary w-full mt-4"
          >
            Back to login
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-base flex items-center justify-center">
      <div className="flex items-center gap-3 text-text-secondary">
        <Loader2 size={18} className="animate-spin" />
        <span className="text-sm">Signing you in…</span>
      </div>
    </div>
  )
}
